import "./simple-grid-list.scss";
import "./simple-grid-list--cta-list.scss";

import { ReactNode } from "react";
import cn from "clsx";

import {
  ComponentHeader,
  ComponentHeaderProps,
} from "design-system/components/primitives/component-header/component-header";
import { Alignment, EditAttributes } from "design-system/types/types";

export interface SimpleGridListItemProps {
  title?: ReactNode;
  content?: ReactNode;

  editAttributes?: {
    title?: EditAttributes;
    description?: EditAttributes;
  };
}

export interface SimpleGridListProps {
  align?: Alignment;
  items: Array<SimpleGridListItemProps>;
  isCTAList?: boolean;
  headerContent?: ComponentHeaderProps;
  HeadingLevel?: "h2" | "h3";
}

/**
 * For displaying a grid of text items
 *
 * ## See it in use on...
 * - A [landing page](/story/example-pages-landing-pages-card-topper--story-alt)
 *
 * - **`id: CB-005-001-00`**
 * - **`data-region: cb__grid-lists__small`**
 */
export function SimpleGridList({
  align = "wide",
  isCTAList,
  items,
  headerContent,
  HeadingLevel = "h2",
}: SimpleGridListProps) {
  if (isCTAList) align = "full";

  const wrapperClass = cn(
    `hbs-global-align-${align}`,
    "hbs-component--simple-grid-list",
  );

  return (
    <div
      className={wrapperClass}
      data-region={`cb__grid-lists__small${isCTAList ? " linklist" : ""}`}
    >
      {headerContent && (
        <ComponentHeader
          {...headerContent}
          anchored={true}
          HeadingLevel={HeadingLevel}
        />
      )}

      <div
        className={cn(
          "hbs-simple-grid-list",
          isCTAList && "hbs-simple-grid-list--cta-list",
        )}
      >
        <ul className="hbs-simple-grid-list__list">
          {items.map(({ title, content, editAttributes }, i) => (
            <li key={i} className="hbs-simple-grid-list__item">
              {title && (
                <div
                  {...editAttributes?.title}
                  className="hbs-simple-grid-list__item-heading"
                >
                  {title}
                </div>
              )}

              {content && (
                <div
                  {...editAttributes?.description}
                  className="hbs-simple-grid-list__content"
                >
                  {content}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
